import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';

import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import range from 'lodash/range';
import orderBy from 'lodash/orderBy';
import last from 'lodash/last';

import moment from 'moment';
import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';

import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import createStatusesFilter from '../components/quick-filters/statuses';

const calendar = createCalendar({
	namespace: 'extThreadsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'year').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const statusesFilter = createStatusesFilter({
	namespace: 'extThreadsList',
	stateNodeName: 'statusesFilter'
});

export const tableViewType = {
	group: 'group',
	list: 'list'
};

// actions types
const FETCH_DATA_SUCCESS = 'ext-threads-list/fetch-data-success';
const FETCH_DATA_ERROR = 'ext-threads-list/fetch-data-error';
const SORT_BY_COLUMN = 'ext-threads-list/table-sort-by-column';
const TOGGLE_PROGRESS_BAR = 'ext-threads-list/toggle-progress-bar';
const GOTO_PAGE_SUCCESS = 'ext-threads-list/goto-page-success';

const APPLY = 'ext-threads-list/apply';
const CLEAR = 'ext-threads-list/clear';

const SWITCH_TABLE_VIEW = 'ext-threads-list/switch-table-view';

const SET_SEARCH_QUERY = 'ext-threads-list/set-search-query';

// permissions
export const allowedPermissions = {
	view: 'ext_threads_list',
	answer: 'ext_threads_answer'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	calendar: calendar.initialState,
	search: '',
	statusesFilter: statusesFilter.initialState,
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	table: {
		view: tableViewType.group,
		columns: {
			updated_at: {
				index: 'updated_at',
				title: 'Date',
				direction: 'desc'
			},
			name: {
				index: 'name',
				title: 'Name',
				direction: 'desc'
			},
			email: {
				index: 'email',
				title: 'Email',
				direction: 'desc'
			},
			body: {
				index: 'body',
				title: 'Text'
			}
		},
		sortByColumn: {
			index: 'updated_at',
			title: 'Date',
			direction: 'desc'
		}
	},
	requestQueryString: '',
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { perPage } = getState().extThreadsList.pagination;
			const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};

			dispatch(actions.fetchRequest(0, perPage, externalQuery && externalQuery.query));
		},

		fetchRequest: (offset = 0, limit = 100, externalQuery) => (dispatch, getState) => {
			const { search, calendar } = getState().extThreadsList;

			const query = qs.stringify({
				offset,
				limit,
				search,
				date_range: getDateRangeFromCalendar(calendar),
				quick_filters: getQuickFilters(getState().extThreadsList)
			});

			return http({
				url: `${endpoints.EXTERNAL_THREADS.LIST}?${query}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: {
					requestQueryString: query
				}
			})(dispatch);
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('threadsSearchForm')(getState());
			const search = searchForm && searchForm.search;

			dispatch(actions.toggleProgressBar());

			setTimeout(
				() =>
					dispatch({
						type: APPLY,
						payload: search
					}),
				100
			);
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());

			dispatch(actions.fetchRequest());
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('threadsSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => (dispatch, getState) => {
			dispatch(replace('external-tickets/'));
		},

		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		redirectToModuleIndex: () => dispatch => {
			// @todo Adhock for redux-form submit functionality.
			setTimeout(() => dispatch(replace('external-tickets/')), 100);
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			const { perPage } = getState().extThreadsList.pagination;
			const offset = (page - 1) * perPage;

			dispatch(actions.toggleProgressBar());

			dispatch(actions.fetchRequest(offset, perPage, null)).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					dispatch({
						type: GOTO_PAGE_SUCCESS,
						payload: page
					});
				}
			});
		},

		switchTableView: viewType => ({
			type: SWITCH_TABLE_VIEW,
			payload: viewType
		})
	},
	calendar.actions,
	statusesFilter.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (
				action.payload.pathname.indexOf('external-tickets') !== -1 &&
				!!action.payload.query &&
				!isEmpty(action.payload.query)
			) {
				return {
					...state,
					data: null
				};
			}

			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data.rows,
				pagination: {
					...state.pagination,
					total: action.payload.data.total
				},
				requestQueryString: action.meta.requestQueryString,
				showProgressBar: false,
				error: false
			};
		},
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[APPLY]: (state, action) => ({ ...state, data: null, showProgressBar: false, search: action.payload }),
		[CLEAR]: state => ({ ...initialState }),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnIndex = action.payload;
			const direction =
				sortByColumn['index'] == columnIndex
					? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
					: columns[columnIndex].direction;
			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SWITCH_TABLE_VIEW]: (state, action) => ({
			...state,
			table: {
				...state.table,
				view: tableViewType[action.payload]
			}
		}),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	calendar.reducers,
	statusesFilter.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.extThreadsList.data,
	getTable: state => {
		const { table, data } = state.extThreadsList;
		const rows = data ? sortColumns(data, table.sortByColumn) : [];

		let tableData = rows;

		if (table.view === tableViewType.group) {
			tableData = groupTableRows(tableData);
		}

		return {
			headers: values(table.columns),
			rows: tableData,
			sortedColumn: table.sortByColumn,
			view: table.view
		};
	},
	getPagination: state => {
		const { perPage, total, currentPage } = state.extThreadsList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getSearchData: state => ({ search: state.extThreadsList.search }),
	getCalendar: state => calendar.selectors.getCalendar(state => state.extThreadsList.calendar, state => state)(state),
	getQuickFilters: state => state.extThreadsList.quickFilters,
	getIntl: state => intl(state),
	getProgressBar: state => state.extThreadsList.showProgressBar,
	getThreadStatuses: state => state.root.lists['thread-statuses'],
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.extThreadsList.error,
	getStatusesFilters: statusesFilter.selectors.getFilters,
	getRequestQueryString: state =>
		`${state.extThreadsList.requestQueryString}&tableView=${state.extThreadsList.table.view}`
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(
		rows,
		row => {
			const value = row[sortedColumn.index];
			switch (sortedColumn.index) {
				case 'updated_at':
					return moment(value).valueOf();
				default:
					return value;
			}
		},
		sortedColumn.direction
	);
};

const getQuickFilters = ({ statusesFilter }) => {
	const prepareFiltersFn = filterList =>
		filterList
			.filter(entity => entity.active)
			.map(entity => entity.name);

	return {
		statuses: prepareFiltersFn(statusesFilter['filters'])
	};
};

const groupTableRows = rows => {
	const res = [
		{ name: 'unanswered', data: [], className: 'without-answer', type: 'unanswered' },
		{ name: 'in work', data: [], className: 'in-work', type: 'in-work' }
	];

	rows.forEach(row => {
		if (isWithoutAnswer(row)) {
			res[0]['data'].push(row);
		} else {
			res[1]['data'].push(row);
		}
	});

	return res;
};

const isWithoutAnswer = row => {
	return !last(row.posts).admin_id;
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/external-threads/list/index.js